/**
 * MessageDetail — the unified "Viestit" detail at `/messages/:id` (030 US2;
 * 050 US2).
 *
 * A single content path: the id is a real asianhallinta conversation, and the
 * shared `member-cases/CaseDetail` renders the LIVE thread + reply + attachments
 * (038/040). An unresolvable / not-owned id lands the member back on the Viestit
 * list (IDOR non-disclosure, FR-003) — never an error surface.
 *
 * The former device-local "sent-record" detail branch (030 deviation A, a
 * read-only summary of a locally-echoed submission) is RETIRED (050 US2): a
 * composed message now creates a REAL case (050 US1), so the member's own
 * message is read back through the same live thread as every other conversation
 * — no browser-local copy, no shadow surface (P1/P7).
 */

import { type JSX, Suspense } from 'react';
import { Navigate, useParams } from 'react-router';

import { DetailSurfaceBackHeader } from '../content/routes/detailSurfaceBackHeader.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { CaseDetailBody } from '../member-cases/CaseDetail.js';
import { PortErrorBoundary } from '../shell/PortErrorBoundary.js';

// At desktop width a single message fills the shell's content column, which
// Layout widens for this route, rather than a narrower cap of its own.
const sectionClasses = 'mx-auto max-w-lg lg:max-w-none px-4 pb-6 pt-back-header';
const noteClasses = 'text-sm text-muted-foreground';

export function MessageDetail(): JSX.Element {
  const t = useTranslate();
  const { id } = useParams<{ id: string }>();

  if (id === undefined) {
    return <Navigate to="/messages" replace />;
  }

  // A real asianhallinta conversation → the live thread/reply/attachments.
  // Not-found / not-owned lands the member on the list (IDOR non-disclosure, FR-003).
  return (
    <>
      <DetailSurfaceBackHeader
        fixedTo="/messages"
        fallbackTo="/messages"
        backLabel={t('myCases.detail.back')}
      />
      <section className={sectionClasses} aria-labelledby="message-detail-title">
        <span id="message-detail-title" className="sr-only">
          {t('myCases.detail.heading')}
        </span>
        <PortErrorBoundary
          portName="memberCases"
          fallback={<p className={noteClasses}>{t('myCases.detail.unavailable')}</p>}
        >
          <Suspense
            fallback={
              <p className={noteClasses} role="status" aria-live="polite">
                {t('myCases.loading')}
              </p>
            }
          >
            <CaseDetailBody caseId={id} notFound={<Navigate to="/messages" replace />} />
          </Suspense>
        </PortErrorBoundary>
      </section>
    </>
  );
}
